/**
 * NovaEmotion — global reactive state for Nova's expression.
 *
 * Components can fire `trigger("celebrating")` when something good
 * happens, and Nova plays the matching animation for a beat before
 * settling back to her baseline. Steady states (the screen-level
 * "this is what Nova is doing right now") use `setBaseline()`.
 *
 * Usage:
 *   const { trigger } = useNovaEmotion();
 *   trigger("celebrating");          // 4s celebration, then back to baseline
 *   trigger("sad", { duration: 6000 }); // 6s sadness
 *
 *   const { state } = useNovaEmotion();
 *   <Nova state={state} />            // displays current state
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { NovaState } from "./Nova";

interface TriggerOptions {
  /** Milliseconds the one-shot reaction holds before reverting. Default 4000. */
  duration?: number;
}

interface NovaEmotionContextValue {
  /** The state Nova should render right now. */
  state: NovaState;
  /** The steady "ambient" state to fall back to after one-shots fade. */
  baseline: NovaState;
  /** Set a new ambient state. Lasts until changed again. */
  setBaseline: (s: NovaState) => void;
  /** Play a one-shot reaction. Reverts to baseline after `duration` ms. */
  trigger: (s: NovaState, opts?: TriggerOptions) => void;
}

const NovaEmotionContext = createContext<NovaEmotionContextValue | null>(null);

const DEFAULT_DURATION = 4000;

export function NovaEmotionProvider({
  children,
  initial = "idle",
}: {
  children: ReactNode;
  initial?: NovaState;
}) {
  const [baseline, setBaselineState] = useState<NovaState>(initial);
  const [oneShot, setOneShot] = useState<NovaState | null>(null);
  const oneShotTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setBaseline = useCallback((s: NovaState) => {
    setBaselineState(s);
  }, []);

  const trigger = useCallback((s: NovaState, opts?: TriggerOptions) => {
    if (oneShotTimer.current) clearTimeout(oneShotTimer.current);
    setOneShot(s);
    const ms = opts?.duration ?? DEFAULT_DURATION;
    oneShotTimer.current = setTimeout(() => {
      setOneShot(null);
      oneShotTimer.current = null;
    }, ms);
  }, []);

  // Tidy up on unmount
  useEffect(() => () => {
    if (oneShotTimer.current) clearTimeout(oneShotTimer.current);
  }, []);

  const value = useMemo<NovaEmotionContextValue>(
    () => ({
      state: oneShot ?? baseline,
      baseline,
      setBaseline,
      trigger,
    }),
    [oneShot, baseline, setBaseline, trigger],
  );

  return (
    <NovaEmotionContext.Provider value={value}>
      {children}
    </NovaEmotionContext.Provider>
  );
}

/**
 * Read or trigger Nova's emotion. Falls back to `idle` baseline if
 * called outside a provider, so components don't crash if the wiring
 * isn't there yet (useful during gradual rollout).
 */
export function useNovaEmotion(): NovaEmotionContextValue {
  const ctx = useContext(NovaEmotionContext);
  if (ctx) return ctx;
  // Fallback — no-op trigger so consumers compile and don't error.
  return {
    state: "idle",
    baseline: "idle",
    setBaseline: () => {},
    trigger: () => {},
  };
}

/**
 * Component-level hook: lock Nova's baseline while this view is mounted.
 * Useful for screens that own a steady "Nova mood" — e.g. the Today
 * screen wants `idle`, the inquiry inbox wants `curious`, etc.
 *
 *   useNovaBaseline("curious"); // Nova goes curious on mount, idle on unmount
 */
export function useNovaBaseline(state: NovaState) {
  const { baseline, setBaseline } = useNovaEmotion();
  useEffect(() => {
    const previous = baseline;
    setBaseline(state);
    return () => setBaseline(previous);
    // baseline intentionally NOT in deps — we only restore the
    // baseline that was active when this hook mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
}
